import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  BadgeOutlined as BadgeIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { getNationalityLabelKey } from '../../constants/nationalities';
import type { TruckSchema } from '../../types/transportation.type';
import type { UserSchema } from '../../types/auth.type';

type DriverData = {
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  gender?: string | null;
  nationality?: string | null;
  date_of_birth?: string | null;
  iban?: string | null;
  driver_license_number?: string | null;
  driver_license_expiry?: string | null;
  vehicle_registration_number?: string | null;
};

export type DriverViewModel = UserSchema & {
  assignedTruck?: TruckSchema | null;
};

type DetailRow = {
  key: string;
  label: string;
  value: React.ReactNode;
  show?: boolean;
  mono?: boolean;
  muted?: boolean;
};

function ViewSectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      sx={{
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'text.secondary',
        display: 'block',
        mb: 1.25,
      }}
    >
      {children}
    </Typography>
  );
}

function DetailValue({
  children,
  muted,
  mono,
  title,
}: {
  children: React.ReactNode;
  muted?: boolean;
  mono?: boolean;
  title?: string;
}) {
  return (
    <Typography
      variant="body2"
      title={title}
      sx={{
        fontWeight: muted ? 400 : 500,
        color: muted ? 'text.secondary' : 'text.primary',
        fontFamily: mono ? 'monospace' : undefined,
        fontSize: mono ? '0.875rem' : undefined,
        wordBreak: mono ? 'break-all' : 'break-word',
        textAlign: 'end',
      }}
    >
      {children}
    </Typography>
  );
}

function DetailLine({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(110px, 40%) 1fr',
        gap: 1.5,
        alignItems: 'baseline',
        py: 0.85,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: 'text.secondary',
        }}
      >
        {label}
      </Typography>
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

function DetailList({ rows }: { rows: DetailRow[] }) {
  const theme = useTheme();
  const visible = rows.filter((row) => row.show !== false);
  if (visible.length === 0) return null;

  return (
    <Stack
      spacing={0}
      divider={
        <Box
          sx={{
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
          }}
        />
      }
    >
      {visible.map((row) => (
        <DetailLine key={row.key} label={row.label}>
          {typeof row.value === 'string' || typeof row.value === 'number' ? (
            <DetailValue muted={row.muted} mono={row.mono} title={String(row.value)}>
              {row.value}
            </DetailValue>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>{row.value}</Box>
          )}
        </DetailLine>
      ))}
    </Stack>
  );
}

function displayField(value: unknown): string {
  if (value == null) return '—';
  const text = String(value).trim();
  return text || '—';
}

function hasText(value: unknown): boolean {
  return value != null && String(value).trim() !== '';
}

function getDriverName(driver: DriverViewModel): string {
  const data = driver.data as DriverData | undefined;
  const first = data?.first_name?.trim();
  const last = data?.last_name?.trim();
  const fullName = [first, last].filter(Boolean).join(' ');
  if (fullName) return fullName;
  return driver.username || driver.phone || data?.phone || '—';
}

function getAccountLabel(driver: DriverViewModel): string {
  return driver.username || driver.phone || (driver.data as DriverData)?.phone || '—';
}

export interface DriverViewDialogProps {
  driver: DriverViewModel | null;
  open: boolean;
  onClose: () => void;
  onEdit?: (driver: DriverViewModel) => void;
}

const DriverViewDialog: React.FC<DriverViewDialogProps> = ({
  driver,
  open,
  onClose,
  onEdit,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const data = (driver?.data ?? {}) as DriverData;
  const phone = driver ? (driver as { phone?: string }).phone ?? data.phone : undefined;
  const displayName = driver ? getDriverName(driver) : '—';
  const accountLabel = driver ? getAccountLabel(driver) : '—';
  const nationalityKey = getNationalityLabelKey(data.nationality);
  const nationalityLabel = nationalityKey
    ? t(nationalityKey)
    : displayField(data.nationality);
  const genderLabel =
    data.gender === 'Male'
      ? t('auth.male')
      : data.gender === 'Female'
        ? t('auth.female')
        : displayField(data.gender);

  const personalRows: DetailRow[] = [
    {
      key: 'firstName',
      label: t('auth.firstName'),
      value: displayField(data.first_name),
      show: hasText(data.first_name),
    },
    {
      key: 'lastName',
      label: t('auth.lastName'),
      value: displayField(data.last_name),
      show: hasText(data.last_name),
    },
    {
      key: 'phone',
      label: t('common.phone'),
      value: displayField(phone),
      show: hasText(phone),
    },
    {
      key: 'gender',
      label: t('auth.gender'),
      value: genderLabel,
      show: hasText(data.gender),
    },
    {
      key: 'nationality',
      label: t('auth.nationality'),
      value: nationalityLabel,
      show: nationalityKey != null || hasText(data.nationality),
    },
    {
      key: 'dob',
      label: t('auth.dateOfBirth'),
      value: displayField(data.date_of_birth),
      show: hasText(data.date_of_birth),
    },
    {
      key: 'iban',
      label: t('auth.iban'),
      value: data.iban?.trim() ?? '',
      show: hasText(data.iban),
      mono: true,
    },
  ];

  const driverRows: DetailRow[] = [
    {
      key: 'license',
      label: t('transportation.drivers.driverLicenseNumber'),
      value: displayField(data.driver_license_number),
      show: true,
      mono: true,
      muted: !hasText(data.driver_license_number),
    },
    {
      key: 'licenseExpiry',
      label: t('transportation.drivers.driverLicenseExpiry'),
      value: displayField(data.driver_license_expiry),
      show: hasText(data.driver_license_expiry),
    },
    {
      key: 'registration',
      label: t('transportation.drivers.vehicleRegistration'),
      value: displayField(data.vehicle_registration_number),
      show: hasText(data.vehicle_registration_number),
      mono: true,
    },
    {
      key: 'truck',
      label: t('transportation.drivers.assignedTruck'),
      value: driver?.assignedTruck ? (
        <Chip
          size="small"
          label={driver.assignedTruck.name}
          sx={{
            height: 26,
            fontWeight: 600,
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            color: theme.palette.primary.main,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
          }}
        />
      ) : (
        <DetailValue muted>{t('transportation.drivers.notAssigned')}</DetailValue>
      ),
      show: true,
    },
  ];

  const hasPersonalSection = personalRows.some((row) => row.show !== false);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 2.5,
          py: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              flexShrink: 0,
            }}
          >
            <BadgeIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, m: 0 }}>
              {t('transportation.drivers.driverDetails')}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              title={displayName}
              sx={{ display: 'block', mt: 0.25 }}
            >
              {displayName}
            </Typography>
            {hasText(accountLabel) && displayName !== accountLabel ? (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                title={accountLabel}
                sx={{ display: 'block' }}
              >
                {accountLabel}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, py: 2 }}>
        {driver ? (
          <Stack spacing={1.5}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: alpha(theme.palette.primary.main, 0.02),
              }}
            >
              <ViewSectionTitle>{t('auth.account')}</ViewSectionTitle>
              <DetailList
                rows={[
                  {
                    key: 'email',
                    label: t('transportation.drivers.usernameEmail'),
                    value: accountLabel,
                    show: true,
                  },
                ]}
              />
            </Box>

            {hasPersonalSection ? (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                }}
              >
                <ViewSectionTitle>{t('auth.personal')}</ViewSectionTitle>
                <DetailList rows={personalRows} />
              </Box>
            ) : null}

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: alpha(theme.palette.primary.main, 0.02),
              }}
            >
              <ViewSectionTitle>{t('transportation.drivers.driverAndVehicle')}</ViewSectionTitle>
              <DetailList rows={driverRows} />
            </Box>
          </Stack>
        ) : null}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          gap: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {t('common.close')}
        </Button>
        {driver && onEdit ? (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => onEdit(driver)}
            sx={{ textTransform: 'none' }}
          >
            {t('common.edit')}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default DriverViewDialog;
